// What a screen shows while it is reading, and what it shows if the read failed.
//
// Shared by both data screens so they are the same two states rather than two
// interpretations of the same two states. Neither of them is "empty": an
// account with nothing yet is a LOADED screen with nothing in it, and each page
// renders that itself, because what "nothing yet" means differs — no usage on
// the metering page, no instances on the fleet page.

import { Card, CardContent } from '@miq/design-system/components/ui/card';

/**
 * The read in flight.
 *
 * Deliberately not a spinner over the finished layout. A skeleton of a table
 * whose row count is unknown implies a row count, and the thing most often
 * being loaded here is an account that has no rows at all.
 */
export function ScreenLoading({ title }: { title: string }) {
  return (
    <div className="p-6">
      <Card>
        <CardContent className="py-8">
          <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground" role="status">
            Loading…
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * The read refused or could not be made.
 *
 * The service's own wording, shown as-is. A message invented here would be a
 * second explanation to keep in step with the first, and the backend words its
 * refusals carefully — one sentence for five conditions, so that a refusal
 * cannot be walked against a customer list.
 */
export function ScreenFailed({ title, message }: { title: string; message: string }) {
  return (
    <div className="p-6">
      <Card>
        <CardContent className="py-8">
          <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground" role="alert">
            {message}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
